import {
  organizationQueryKey,
  useMutationDeleteOrganization,
} from "@apis:organization/organization";
import { ConfirmationDialog } from "@components:ConfirmationDialog";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useToasterStoreActions } from "@stores:global";
import { useTranslation } from "react-i18next";

interface OrganizationDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
}

export const OrganizationDeleteModal = ({
  isOpen,
  onClose,
  organizationId,
}: OrganizationDeleteModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const mutationDeleteOrganization = useMutationDeleteOrganization({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.organizations.deleted"),
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: organizationQueryKey.getAll.queryKey,
        },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.organizations.deleteFailed"),
        variant: "error",
      });
    },
  });

  const handleDelete = async () => {
    await mutationDeleteOrganization.mutateAsync({ id: organizationId });
  };

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={() => onClose()}
      title={t("tenant.organizations.deleteTitle")}
      description={t("tenant.organizations.deleteConfirm")}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
            backgroundColor: (theme) => theme.palette.error.main,
          },
          label: t("common.delete"),
          onClick: async () => {
            await handleDelete();
          },
          disabled: !organizationId,
        },
      }}
    />
  );
};
